// API surface for the MS 990 Batch Extension utility.

export interface MS990ExtCandidate {
  // Whatever the 990extensions Logic App returns. We treat unknown columns as strings.
  ReturnID: string;
  ClientCode: string;     // hyphenated "NNNNN-NNN"
  ClientName: string;
  Job_Name: string;
  Job_Idx?: string;
  [key: string]: string | undefined;
}

export interface ProcessStep {
  name: string;
  status: 'ok' | 'skipped' | 'error';
  message?: string;
  // Step-specific extras (returnType, value, units, sessionGuid, objectId, ...)
  [key: string]: unknown;
}

export interface ProcessResult {
  returnId: string;
  clientCode: string;
  alreadyAccepted?: boolean;
  skippedEfile?: boolean;
  // 'skipped' = pre-flight blocker (in-use / prohibit-changes); nothing was attempted.
  // 'error'   = a step actually failed mid-pipeline.
  overallStatus: 'success' | 'skipped' | 'error';
  error?: string;
  steps: ProcessStep[];
}

/**
 * Fetch the audit log of previously-completed return IDs so we can filter them
 * out of fresh candidate pulls. Empty set if nothing has run successfully yet.
 */
export async function fetchCompletedAudit(): Promise<Set<string>> {
  const res = await fetch('/api/util-ms990ext-audit', { credentials: 'include' });
  if (!res.ok) {
    // Don't break Pull on audit-read errors — just behave as if nothing's been completed.
    return new Set();
  }
  const data = await res.json().catch(() => ({}));
  const arr = Array.isArray((data as any).completed) ? (data as any).completed : [];
  return new Set(arr as string[]);
}

/** Pull the list of 990 returns awaiting MS extension transmission via the Logic App. */
export async function pullCandidates(): Promise<MS990ExtCandidate[]> {
  const res = await fetch('/api/portal-data?type=990extensions', { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Pull failed (HTTP ${res.status})`);
  }
  const data = await res.json();
  // Logic App typically returns { ResultSets: { Table1: [...] } } — handle both shapes.
  if (Array.isArray(data)) return data as MS990ExtCandidate[];
  const t1 = (data as any)?.ResultSets?.Table1;
  if (Array.isArray(t1)) return t1 as MS990ExtCandidate[];
  const arr = (data as any)?.rows;
  if (Array.isArray(arr)) return arr as MS990ExtCandidate[];
  throw new Error('Unexpected portal-data response shape');
}

/** Run the full pipeline for one return. */
export async function processOne(
  candidate: Pick<MS990ExtCandidate, 'ReturnID' | 'ClientName' | 'ClientCode' | 'Job_Name'>,
): Promise<ProcessResult> {
  const res = await fetch('/api/util-ms990ext-process', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      returnId:   candidate.ReturnID,
      clientName: candidate.ClientName,
      clientCode: candidate.ClientCode,
      jobName:    candidate.Job_Name,
    }),
  });
  // The function returns 200 even on per-return failure — error info is in the body.
  if (!res.ok && res.status !== 200) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).error || `Server error ${res.status}`);
  }
  return res.json();
}
